/**
 * Library authoring — generic demo constants for the new-item composer.
 *
 * Storage in `lib/mock/library.ts` keeps the catalog `LibraryItemType` enum
 * (choice, written, fill-in, matching, hotspot). The authoring surface
 * exposes a richer item-writer model and maps back to that base enum on save.
 *
 * All copy is intentionally generic so the demo applies to any product domain.
 */

import type { LibraryBloomLevel, LibraryItemType } from "@/lib/mock/library"
import {
  STORAGE_TYPE_BY_COMPOSER_TYPE,
  type ComposerQuestionType,
} from "@/lib/question-type"
import { leadInPlainText } from "@/lib/lead-in-html"
import { isLeoQuestionEditDirective } from "@/lib/question-quality/leo-edit-directive"

// ─────────────────────────────────────────────────────────────────────────────
// Item type
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Authoring-time item type — richer than the storage enum.
 *
 * Which storage type each one is filed as lives in `lib/question-type`
 * (`STORAGE_TYPE_BY_COMPOSER_TYPE`), with the reverse map beside it, so the
 * pool round-trip and the storage round-trip cannot disagree.
 */
export type AuthoringQuestionType = ComposerQuestionType

export interface AuthoringQuestionTypeOption {
  value: AuthoringQuestionType
  label: string
  shortLabel: string
  icon: string
  /** Long, sentence-form description for the inspector caption. */
  description: string
  /** Compact one-liner shown under the title in `SelectionTileGrid` tiles. */
  tileSummary: string
  /** "Single best answer", "Multiple response", etc. — appears under the title. */
  badge?: string
  /** Maps to the storage type when persisting. */
  storageType: LibraryItemType
}

export const AUTHORING_QUESTION_TYPES: readonly AuthoringQuestionTypeOption[] =
  [
    {
      value: "mcq_single",
      label: "Single choice",
      shortLabel: "Single choice",
      icon: "fa-list-radio",
      description:
        "One correct answer from a set of options. Default 5 distractors.",
      tileSummary: "Single correct answer from a set of options",
      badge: "Single best answer",
      storageType: "multiple_choice",
    },
    {
      value: "mcq_multiple",
      label: "Multi-select",
      shortLabel: "Multi-select",
      icon: "fa-list-check",
      description:
        "Select every correct option from the list. Optional partial credit.",
      tileSummary: "One or more correct answers — partial credit",
      badge: "Multiple response",
      storageType: STORAGE_TYPE_BY_COMPOSER_TYPE.mcq_multiple,
    },
    {
      value: "true_false",
      label: "True / False",
      shortLabel: "True / False",
      icon: "fa-circle-half-stroke",
      description: "Use for simple, unambiguous statements.",
      tileSummary: "Binary statement — quick recall",
      storageType: "true_false",
    },
    {
      value: "short_answer",
      label: "Short answer",
      shortLabel: "Short answer",
      icon: "fa-input-text",
      description:
        "Free-text response with an expected answer + acceptable variants.",
      tileSummary: "Single word or short phrase, exact-match",
      storageType: "short_answer",
    },
    {
      value: "numeric",
      label: "Numeric",
      shortLabel: "Numeric",
      icon: "fa-input-numeric",
      description:
        "Number-only response with optional units and a ± tolerance band.",
      tileSummary: "Number with units and tolerance band",
      badge: "Auto-graded",
      storageType: "short_answer",
    },
    {
      value: "essay",
      label: "Essay",
      shortLabel: "Essay",
      icon: "fa-paragraph",
      description: "Long-form response graded against a rubric.",
      tileSummary: "Long response — rubric-graded",
      badge: "Rubric",
      storageType: "essay",
    },
    {
      value: "fill_blank",
      label: "Fill in the blank",
      shortLabel: "Fill in the blank",
      icon: "fa-text-size",
      description:
        "Sentence with one or more {{blanks}}; respondents type the missing word(s).",
      tileSummary: "Sentence with one or more blanks to fill",
      storageType: "fill_blank",
    },
    {
      value: "matching",
      label: "Matching",
      shortLabel: "Matching",
      icon: "fa-arrow-right-arrow-left",
      description:
        "Two columns — pair each prompt on the left to its correct match on the right.",
      tileSummary: "Pair entries on the left to entries on the right",
      storageType: "matching",
    },
    {
      value: "ordering",
      label: "Ordering",
      shortLabel: "Ordering",
      icon: "fa-list-ol",
      description:
        "Show steps; respondents re-order them into the correct sequence.",
      tileSummary: "Place items in the correct sequence",
      storageType: STORAGE_TYPE_BY_COMPOSER_TYPE.ordering,
    },
    {
      value: "hotspot",
      label: "Hotspot",
      shortLabel: "Hotspot",
      icon: "fa-bullseye-pointer",
      description: "Image-based item — respondents click the correct region.",
      tileSummary: "Click the correct region of an image",
      badge: "Image-based",
      storageType: "hotspot",
    },
  ] as const

export function authoringQuestionType(
  value: AuthoringQuestionType
): AuthoringQuestionTypeOption {
  return (
    AUTHORING_QUESTION_TYPES.find((t) => t.value === value) ??
    AUTHORING_QUESTION_TYPES[0]
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Level + tier + cognitive level
// ─────────────────────────────────────────────────────────────────────────────

export const AUTHORING_DIFFICULTY_OPTIONS = [
  {
    value: "easy",
    label: "Low",
    description: "Quick recall. Most respondents complete",
  },
  {
    value: "medium",
    label: "Normal",
    description: "Standard application. Mixed completion",
  },
  {
    value: "hard",
    label: "High",
    description: "Deeper analysis. Fewer respondents complete",
  },
] as const

export const AUTHORING_BLOOM_OPTIONS: readonly {
  value: LibraryBloomLevel
  label: string
  hint: string
}[] = [
  { value: "Remember", label: "Remember", hint: "Recall basic facts" },
  {
    value: "Understand",
    label: "Understand",
    hint: "Explain, summarise, classify",
  },
  { value: "Apply", label: "Apply", hint: "Use knowledge in a new situation" },
  {
    value: "Analyze",
    label: "Analyze",
    hint: "Break information apart, examine relationships",
  },
  {
    value: "Evaluate",
    label: "Evaluate",
    hint: "Justify a stand, defend a decision",
  },
  { value: "Create", label: "Create", hint: "Produce new or original work" },
] as const

/** Cognitive level — broader bucketing for analytics. */
export const AUTHORING_COG_LEVEL_OPTIONS = [
  { value: "recall", label: "Recall", hint: "Definitions and core concepts" },
  {
    value: "application",
    label: "Application",
    hint: "Apply concept to a new scenario",
  },
  {
    value: "analysis",
    label: "Analysis",
    hint: "Compare, evaluate, synthesise",
  },
] as const
export type AuthoringCogLevel =
  (typeof AUTHORING_COG_LEVEL_OPTIONS)[number]["value"]

// ─────────────────────────────────────────────────────────────────────────────
// Category / subject / track / phase
// ─────────────────────────────────────────────────────────────────────────────

export const AUTHORING_SUBJECT_AREAS = [
  "Category 1",
  "Category 2",
  "Category 3",
  "Category 4",
  "Category 5",
  "Category 6",
  "Category 7",
  "Category 8",
  "Category 9",
  "Category 10",
] as const
export type AuthoringSubjectArea = (typeof AUTHORING_SUBJECT_AREAS)[number]

export const AUTHORING_BODY_SYSTEMS = [
  { value: "topic-a", label: "Topic A", icon: "fa-circle" },
  { value: "topic-b", label: "Topic B", icon: "fa-circle" },
  { value: "topic-c", label: "Topic C", icon: "fa-circle" },
  { value: "topic-d", label: "Topic D", icon: "fa-circle" },
  { value: "topic-e", label: "Topic E", icon: "fa-circle" },
  { value: "topic-f", label: "Topic F", icon: "fa-circle" },
  { value: "topic-g", label: "Topic G", icon: "fa-circle" },
  { value: "topic-h", label: "Topic H", icon: "fa-circle" },
  { value: "topic-i", label: "Topic I", icon: "fa-circle" },
  { value: "topic-j", label: "Topic J", icon: "fa-circle" },
  { value: "topic-k", label: "Topic K", icon: "fa-circle" },
  { value: "topic-l", label: "Topic L", icon: "fa-circle" },
  { value: "topic-multi", label: "Multi-topic", icon: "fa-circle-nodes" },
] as const
export type AuthoringBodySystem =
  (typeof AUTHORING_BODY_SYSTEMS)[number]["value"]

export const AUTHORING_DISCIPLINES = [
  "Track 1",
  "Track 2",
  "Track 3",
  "Track 4",
  "Track 5",
  "Track 6",
  "Track 7",
  "Track 8",
  "Track 9",
  "Track 10",
  "Track 11",
] as const
export type AuthoringDiscipline = (typeof AUTHORING_DISCIPLINES)[number]

export const AUTHORING_PHASES = [
  { value: "preclinical", label: "Phase 1", hint: "Early stage" },
  { value: "clinical", label: "Phase 2", hint: "Practice stage" },
  { value: "residency", label: "Phase 3", hint: "Advanced stage" },
  { value: "ce", label: "Phase 4", hint: "Ongoing stage" },
] as const
export type AuthoringPhase = (typeof AUTHORING_PHASES)[number]["value"]

/** Default curriculum metadata for demo drafts (Leo + new-item defaults). */
export const AUTHORING_DEFAULT_CURRICULUM = {
  subjectArea: "Category 1" as AuthoringSubjectArea,
  track: "Track 1" as AuthoringDiscipline,
  phase: "preclinical" as AuthoringPhase,
}

// ─────────────────────────────────────────────────────────────────────────────
// Inspector — general / delivery options (assessment delivery)
// ─────────────────────────────────────────────────────────────────────────────

export type AuthoringGeneralSettingKey =
  | "randomizeOptions"
  | "caseSensitive"
  | "negativeMarking"
  | "showFeedbackWhenReviewing"
  | "eligibleForRandomDraw"
  | "shuffleMatchingPairs"
  | "shuffleOrderingItems"
  | "unlimitedSelections"

export type AuthoringGeneralSettings = Record<
  AuthoringGeneralSettingKey,
  boolean
>

/** How multi-select / matching items award marks (PRD US-3). */
export type AuthoringMultiSelectScoring =
  | "exact"
  | "partial"
  | "partial_plus_minus"

export const AUTHORING_MULTI_SELECT_SCORING_OPTIONS = [
  {
    value: "exact" as const,
    label: "Exact answer",
    description:
      "Students must select all correct options and no incorrect options to receive credit.",
  },
  {
    value: "partial" as const,
    label: "Partial credit",
    description:
      "Students receive proportional credit for each correct selection.",
  },
  {
    value: "partial_plus_minus" as const,
    label: "+/- Partial credit",
    description:
      "Students receive credit for correct selections and lose credit for incorrect selections.",
  },
] as const

export type UnlimitedSelectionsPolicy = "optional" | "forced_on" | "forced_off"

export function unlimitedSelectionsPolicy(
  mode: AuthoringMultiSelectScoring,
): UnlimitedSelectionsPolicy {
  if (mode === "partial") return "forced_off"
  if (mode === "partial_plus_minus") return "forced_on"
  return "optional"
}

/** Keep unlimited selections in sync with the scoring mode rules. */
export function resolveUnlimitedSelections(
  mode: AuthoringMultiSelectScoring,
  current: boolean,
): boolean {
  const policy = unlimitedSelectionsPolicy(mode)
  if (policy === "forced_on") return true
  if (policy === "forced_off") return false
  return current
}

/** Map legacy Leo/import boolean toggles onto the scoring enum. */
export function multiSelectScoringFromLegacyToggles(opts: {
  partialCredit?: boolean
  negativeMarking?: boolean
}): AuthoringMultiSelectScoring {
  if (opts.negativeMarking) return "partial_plus_minus"
  if (opts.partialCredit) return "partial"
  return "exact"
}

export const AUTHORING_DEFAULT_MULTI_SELECT_SCORING: AuthoringMultiSelectScoring =
  "exact"

export const AUTHORING_DEFAULT_GENERAL_SETTINGS: AuthoringGeneralSettings = {
  randomizeOptions: true,
  caseSensitive: false,
  negativeMarking: false,
  showFeedbackWhenReviewing: true,
  eligibleForRandomDraw: true,
  shuffleMatchingPairs: true,
  shuffleOrderingItems: false,
  unlimitedSelections: false,
}

export interface AuthoringInspectorToggleOption {
  key: AuthoringGeneralSettingKey
  label: string
  description: string
  /** Omit to show for every question type. */
  applicableTypes?: readonly AuthoringQuestionType[]
}

/** Toggle rows rendered in the composer inspector (filtered by question type). */
export const AUTHORING_INSPECTOR_GENERAL_TOGGLES: readonly AuthoringInspectorToggleOption[] =
  [
    {
      key: "randomizeOptions",
      label: "Randomize options",
      description:
        "Shuffle answer choices for each respondent. Fixed order when off.",
      applicableTypes: ["mcq_single", "mcq_multiple", "true_false"],
    },
    {
      key: "caseSensitive",
      label: "Case sensitive",
      description: "Short-answer grading must match letter casing exactly.",
      applicableTypes: ["short_answer", "fill_blank"],
    },
    {
      key: "shuffleMatchingPairs",
      label: "Randomize match order",
      description: "Shuffle the right-hand column for each respondent.",
      applicableTypes: ["matching"],
    },
    {
      key: "shuffleOrderingItems",
      label: "Randomize step order",
      description:
        "Present ordering steps in a shuffled sequence to respondents.",
      applicableTypes: ["ordering"],
    },
    {
      key: "showFeedbackWhenReviewing",
      label: "Show feedback when reviewing",
      description:
        "Reveal rationale and keyed answers after the attempt is submitted.",
    },
  ] as const

export function authoringInspectorToggleVisible(
  option: AuthoringInspectorToggleOption,
  type: AuthoringQuestionType
): boolean {
  if (!option.applicableTypes) return true
  return option.applicableTypes.includes(type)
}

/** Default point value when an item is placed on an assessment (demo). */
export const AUTHORING_DEFAULT_POINTS = "1"

// ─────────────────────────────────────────────────────────────────────────────
// Workflow status
// ─────────────────────────────────────────────────────────────────────────────

export type AuthoringStatus =
  "draft" | "in_review" | "approved" | "published" | "retired"

export const AUTHORING_STATUS_OPTIONS: readonly {
  value: AuthoringStatus
  label: string
  description: string
  icon: string
}[] = [
  {
    value: "draft",
    label: "Draft",
    description: "Visible only to owners. Not eligible for use.",
    icon: "fa-file-pen",
  },
  {
    value: "in_review",
    label: "In review",
    description: "Awaiting peer review.",
    icon: "fa-magnifying-glass",
  },
  {
    value: "approved",
    label: "Approved",
    description: "Reviewer signed off. Ready to publish.",
    icon: "fa-circle-check",
  },
  {
    value: "published",
    label: "Published",
    description: "Live in the library. Available for use.",
    icon: "fa-broadcast-tower",
  },
  {
    value: "retired",
    label: "Retired",
    description: "Removed from active use. Kept for history.",
    icon: "fa-box-archive",
  },
] as const

// ─────────────────────────────────────────────────────────────────────────────
// Defaults
// ─────────────────────────────────────────────────────────────────────────────

export const AUTHORING_DEFAULT_OPTION_COUNT = 5
export const AUTHORING_MIN_OPTION_COUNT = 2
export const AUTHORING_MAX_OPTION_COUNT = 8

/** Reference row kinds — material students may see while answering. */
export type AuthoringReferenceKind = "citation" | "link" | "image" | "document"

export type AuthoringReference = {
  id: string
  kind: AuthoringReferenceKind
  /** Text citation (kind `citation`). */
  citation: string
  /** URL (kind `link`). */
  url: string
  /** Optional student-facing caption for link / image / document. */
  linkLabel: string
  fileName: string
  /** Local blob URL for image preview in the demo composer. */
  previewUrl: string
}

export const AUTHORING_REFERENCE_KIND_OPTIONS = [
  {
    value: "citation" as const,
    label: "Citation",
    icon: "fa-book",
    description: "Text citation reviewers can verify",
  },
  {
    value: "link" as const,
    label: "Link",
    icon: "fa-link",
    description: "URL students may open while answering",
  },
  {
    value: "image" as const,
    label: "Image",
    icon: "fa-image",
    description: "Figure or photo shown with the question",
  },
  {
    value: "document" as const,
    label: "Document",
    icon: "fa-file-lines",
    description: "PDF or file students may read",
  },
] as const

export function createAuthoringReference(
  id: string,
  kind: AuthoringReferenceKind = "citation"
): AuthoringReference {
  return {
    id,
    kind,
    citation: "",
    url: "",
    linkLabel: "",
    fileName: "",
    previewUrl: "",
  }
}

/** Minimum question length before option suggestions appear in the composer. */
export const OPTION_SUGGESTION_MIN_LEAD_IN = 12

/**
 * Demo suggestions for empty MCQ options — derived from the question stem.
 * In production this would come from Leo / item-writing models.
 */
export function suggestOptionTextsForLeadIn(
  leadIn: string,
  count: number,
  type: AuthoringQuestionType
): string[] {
  const stem = leadInPlainText(leadIn)
  if (stem.length < OPTION_SUGGESTION_MIN_LEAD_IN || type === "true_false") {
    return Array.from({ length: count }, () => "")
  }

  const topic =
    stem
      .replace(
        /^(which|what|how|when|where|who)\s+(of the following\s+)?(is|are|best|most likely|the correct)\s+/i,
        ""
      )
      .replace(/\?+$/, "")
      .trim()
      .slice(0, 72) || "this scenario"

  const singlePool = [
    `Follow the guideline-recommended first-line approach for ${topic}`,
    `Order indicated diagnostics before changing management`,
    `Choose supportive care only and defer indicated workup`,
    `Start broad therapy before confirming the diagnosis`,
    `Reassure and discharge without the usual follow-up`,
    `Select the contraindicated intervention for the presentation`,
    `Prioritize comfort measures over standard-of-care steps`,
    `Treat a look-alike diagnosis while skipping confirmatory testing`,
  ]

  const multiPool = [
    `Include evidence-based steps that apply to ${topic}`,
    `Select actions that delay necessary evaluation or treatment`,
    `Include both required monitoring and indicated first-line therapy`,
    `Choose options that omit standard safety checks`,
    `Pick interventions supported by current consensus guidance`,
    `Include a step appropriate only after specialist referral`,
    `Select redundant tests that do not change management`,
    `Mark both patient education and appropriate follow-up planning`,
  ]

  const pool = type === "mcq_multiple" ? multiPool : singlePool

  let h = 0
  for (let i = 0; i < stem.length; i++) h = (h * 31 + stem.charCodeAt(i)) >>> 0

  return Array.from(
    { length: count },
    (_, idx) => pool[(h + idx * 7) % pool.length]!
  )
}

/** Name placeholder — generic primary line for the demo composer. */
export const AUTHORING_STEM_PLACEHOLDER = `Item 13 — sample primary line for a new library entry. Describe the item in one or two sentences so respondents understand the prompt without needing extra context. Use placeholder content that demonstrates the layout without committing to a specific product domain.`

export const AUTHORING_LEAD_IN_PLACEHOLDER = "Which option fits best?"

/** Best-practice notes prompt — owner-side, not respondent-facing. */
export const AUTHORING_RATIONALE_PLACEHOLDER =
  "Explain why this option is correct (or why this distractor is plausible). Cite the source or reference where possible."

/**
 * Draft item handle assigned when the composer route loads.
 * Format: `LIB-YYMM-XXXX` (e.g. `LIB-2605-A3F2`). Generate on the server and pass
 * into the composer so SSR and hydration share one value.
 */
export function generateDraftQuestionId(now = new Date()): string {
  const yy = String(now.getFullYear()).slice(-2)
  const mm = String(now.getMonth() + 1).padStart(2, "0")
  const rand = Math.random()
    .toString(16)
    .slice(2, 6)
    .toUpperCase()
    .padEnd(4, "0")
  return `LIB-${yy}${mm}-${rand}`
}

/** Fallback PageHeader `<h1>` before the author types a question prompt. */
export const NEW_QUESTION_HEADER_FALLBACK = "New question"

/** Live composer title — collapses whitespace; falls back when the prompt is empty. */
export function questionComposerHeaderTitle(
  leadIn: string | undefined,
  fallback = NEW_QUESTION_HEADER_FALLBACK
): string {
  const normalized = leadInPlainText(leadIn ?? "")
  return normalized.length > 0 ? normalized : fallback
}

// ─────────────────────────────────────────────────────────────────────────────
// Ask Leo — draft assistance
// ─────────────────────────────────────────────────────────────────────────────

/** Hub landing + generic “draft with AI” entry. */
export const LIBRARY_DRAFT_WITH_AI_PROMPT =
  "Help me draft a new library question. Ask what I am assessing, the item format, and difficulty, then propose a stem, answer options (or type-specific fields), rationale, Bloom level, tags, and notes I can paste into the composer."

/** Starter prompts when the new-question composer is open. */
export const NEW_QUESTION_ASK_LEO_SUGGESTIONS = [
  "Draft a single-choice question for this folder with four distractors",
  "Improve my stem and suggest plausible wrong answers",
  "Suggest Bloom level, cognitive level, and tags for this item",
  "Write rationale and references for a clinical scenario question",
] as const

export function buildNewQuestionDraftLeoPrompt(input: {
  draftQuestionId: string
  formatLabel: string
  folderName?: string
  stem?: string
}): string {
  const parts = [
    `I'm in the new-question composer (draft id ${input.draftQuestionId}).`,
    `Question format: ${input.formatLabel}.`,
  ]
  if (input.folderName) parts.push(`Location folder: ${input.folderName}.`)
  const stem = input.stem?.trim()
  if (stem) parts.push(`Stem so far: "${stem}"`)
  else parts.push("The question stem is still empty.")
  parts.push(
    "Propose a complete draft I can paste into the composer: stem, type-specific answers, rationale, Bloom level, cognitive level, tags, and predicted difficulty. Ask one clarifying question if needed."
  )
  return parts.join(" ")
}

/** Aligns with `useLeoThread` mock reply delay — inline composer draft UX. */
export const LEO_QUESTION_DRAFT_DELAY_MS = 3500

/** Fields Leo can populate inline on the new-question composer (demo mock). */
export type LeoQuestionDraftPatch = {
  /** Short name for the bank grid; derived from the prompt when Leo omits it. */
  title?: string
  type?: AuthoringQuestionType
  leadIn?: string
  options?: {
    id: string
    text: string
    isCorrect: boolean
    rationale: string
  }[]
  rationale?: string
  references?: AuthoringReference[]
  numericValue?: string
  numericTolerance?: string
  numericUnits?: string
  pairs?: { id: string; left: string; right: string }[]
  orderedItems?: { id: string; text: string }[]
  fillBlankAnswers?: { id: string; accepted: string }[]
  difficulty?: "easy" | "medium" | "hard"
  subjectArea?: AuthoringSubjectArea | ""
  track?: AuthoringDiscipline | ""
  phase?: AuthoringPhase | ""
  randomizeOptions?: boolean
  /** @deprecated Prefer multiSelectScoring; still accepted from Leo patches. */
  partialCredit?: boolean
  caseSensitive?: boolean
  /** @deprecated Prefer multiSelectScoring for MSQ/matching. */
  negativeMarking?: boolean
  multiSelectScoring?: AuthoringMultiSelectScoring
  unlimitedSelections?: boolean
  showFeedbackWhenReviewing?: boolean
  eligibleForRandomDraw?: boolean
  shuffleMatchingPairs?: boolean
  shuffleOrderingItems?: boolean
  points?: string
  bloom?: string
  cogLevel?: string
  tags?: string[]
  categoryIds?: string[]
}

function leoFolderTag(folderName?: string): string {
  if (!folderName) return "leo-draft"
  const slug = folderName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
  return slug ? `leo-${slug}` : "leo-draft"
}

/**
 * Names a drafted question after its own prompt — trimmed to the first clause,
 * which is how authors abbreviate questions when they talk about them.
 */
export function questionTitleFromLeadIn(leadIn: string): string {
  const prompt = leadInPlainText(leadIn)
    .replace(/\{\{blank\}\}/gi, "")
    .trim()
  if (!prompt) return ""
  const clause = prompt.split(/[.?!:;]/)[0].trim() || prompt
  const words = clause.split(" ")
  const short = words.slice(0, 7).join(" ")
  return words.length > 7 ? `${short}\u2026` : short
}

// ─────────────────────────────────────────────────────────────────────────────
// Leo question brief — what the author asks Leo for before it drafts
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What the author wants Leo to draft. Read out of their own sentence by
 * {@link parseLeoQuestionAsk} — the author types "write a harder Apply item on
 * beta blockade", not three form fields.
 */
export interface LeoQuestionBrief {
  /** Free text: the topic, concept, or scenario the item should cover. */
  coverage: string
  difficulty: (typeof AUTHORING_DIFFICULTY_OPTIONS)[number]["value"]
  bloom: LibraryBloomLevel
}

/** Prompt shapes per Bloom level, so the demanded level shows in the wording. */
const LEO_STEM_BY_BLOOM: Record<
  LibraryBloomLevel,
  (subject: string) => string
> = {
  Remember: (s) => `Which statement best defines ${s}?`,
  Understand: (s) => `Which statement best explains ${s}?`,
  Apply: (s) =>
    `A nurse is responsible for ${s}. Which action is indicated first?`,
  Analyze: (s) =>
    `Which finding best confirms that ${s} was done correctly?`,
  Evaluate: (s) =>
    `Which plan for ${s} is most defensible?`,
  Create: (s) =>
    `Which plan best supports safe ${s}?`,
}

const LEO_TITLE_BY_BLOOM: Record<LibraryBloomLevel, string> = {
  Remember: "definition",
  Understand: "mechanism",
  Apply: "first-line management",
  Analyze: "differentiating findings",
  Evaluate: "plan justification",
  Create: "monitoring plan",
}

/**
 * Demo draft shaped by the author's brief. The canned payload per question type
 * still supplies options, rationale, and settings; the brief overrides the parts
 * the author actually asked about so the result reads like their request.
 */
export function buildLeoQuestionDraftForBrief(
  type: AuthoringQuestionType,
  brief: LeoQuestionBrief,
  folderName?: string
): LeoQuestionDraftPatch {
  const base = buildMockLeoQuestionDraft(type, folderName)
  const subject = brief.coverage.replace(/\s+/g, " ").trim()
  if (!subject)
    return { ...base, difficulty: brief.difficulty, bloom: brief.bloom }

  const leadIn = LEO_STEM_BY_BLOOM[brief.bloom](subject)
  return {
    ...base,
    leadIn,
    // Answers have to be about the same thing as the stem — canned options
    // against a subject-specific prompt read as a broken draft.
    options: base.options && leoOptionsForSubject(base.options, subject),
    rationale: base.options
      ? `The best answer keeps ${subject} central; the distractors defer it, assume stability, or undo the last change without evidence.`
      : base.rationale,
    title: `${subject[0].toUpperCase()}${subject.slice(1)} — ${
      LEO_TITLE_BY_BLOOM[brief.bloom]
    }`,
    difficulty: brief.difficulty,
    bloom: brief.bloom,
  }
}

const LEO_CORRECT_OPTION_BY_SUBJECT: readonly ((s: string) => string)[] = [
  (s) => `Reassess ${s} before escalating treatment`,
  (s) => `Document ${s} and adjust the plan with the team`,
  (s) => `Confirm ${s} with a second, independent check`,
]

const LEO_DISTRACTOR_BY_SUBJECT: readonly ((s: string) => string)[] = [
  (s) => `Escalate treatment and revisit ${s} later`,
  (s) => `Treat ${s} as stable whenever vital signs are normal`,
  (s) => `Reverse the last change regardless of ${s}`,
]

/** Keeps each option's id and correctness; only the text follows the subject. */
function leoOptionsForSubject(
  options: NonNullable<LeoQuestionDraftPatch["options"]>,
  subject: string
) {
  let correct = 0
  let distractor = 0
  return options.map((option) => {
    const text = option.isCorrect
      ? LEO_CORRECT_OPTION_BY_SUBJECT[
          correct++ % LEO_CORRECT_OPTION_BY_SUBJECT.length
        ](subject)
      : LEO_DISTRACTOR_BY_SUBJECT[
          distractor++ % LEO_DISTRACTOR_BY_SUBJECT.length
        ](subject)
    return { ...option, text }
  })
}

// ─────────────────────────────────────────────────────────────────────────────
// Reading the ask out of a sentence
// ─────────────────────────────────────────────────────────────────────────────

/** Ordered hardest → easiest so "not that easy" still reads as a difficulty. */
const LEO_DIFFICULTY_HINTS: readonly [
  RegExp,
  LeoQuestionBrief["difficulty"],
][] = [
  [/\b(harder|hard|challenging|difficult|tougher|tough|stretch|high)\b/i, "hard"],
  [/\b(easier|easy|simple|simpler|straightforward|basic|low)\b/i, "easy"],
  [/\b(normal|moderate|medium|mid-?level)\b/i, "medium"],
]

/** Bloom's read from the verb the author used, highest level first. */
const LEO_BLOOM_HINTS: readonly [RegExp, LibraryBloomLevel][] = [
  [
    /\b(design|compose|construct|devise)\b|\bcreate\b(?!\s+(?:a\s+|the\s+)?(?:question|item|mcq|prompt|stem))/i,
    "Create",
  ],
  [/\b(evaluate|justify|defend|critique|judge|weigh)\b/i, "Evaluate"],
  [/\b(analy[sz]e|differentiate|distinguish|compare|contrast)\b/i, "Analyze"],
  [
    /\b(apply|applied|manage|management|treat|first[-\s]?line|next step|indicated)\b/i,
    "Apply",
  ],
  [
    /\b(understand|explain|describe|mechanism|pathophysiology|why)\b/i,
    "Understand",
  ],
  [/\b(remember|recall|define|definition|list|name|identify)\b/i, "Remember"],
]

/**
 * Framing words — the ask, not its subject. Stripped only from the *front* of
 * the sentence: "indications for anticoagulation" has to keep its "for", and
 * the subject is always what is left after the framing runs out.
 */
const LEO_ASK_FRAMING = new Set(
  `hey hi ok okay leo please can could you i we need want let lets draft write
   create make generate give me build come up with another a an the some one new
   it this that question questions item items stem stems prompt prompts mcq mcqs
   multiple choice single essay short answer true false bloom blooms difficulty
   level levels version variant variants at on about covering for regarding
   around involving to into using of push move bump raise lower try different
   angle shorter longer same again please version
   harder hard high easier easy low simple simpler straightforward basic normal moderate
   medium mid challenging difficult tough tougher stretch
   remember recall define definition list name identify understand explain
   describe mechanism pathophysiology why apply applied manage management treat
   indicated analyze analyse differentiate distinguish compare contrast evaluate
   justify defend critique judge weigh create design compose construct devise`
    .split(/\s+/)
    .filter(Boolean)
)

/** A token is framing when every word inside it is — catches "Apply-level". */
function isLeoFramingToken(token: string) {
  const words = token
    .toLowerCase()
    .replace(/[^a-z'\s-]/g, "")
    .split(/[-\s]+/)
    .filter(Boolean)
  return words.length > 0 && words.every((word) => LEO_ASK_FRAMING.has(word))
}

/**
 * Turns "make it a harder Apply-level item on beta blockade" into a brief.
 *
 * Anything the sentence does not say falls back to `defaults` — which is what
 * makes follow-up turns work: "make it harder" keeps the previous subject and
 * only moves difficulty.
 */
export function parseLeoQuestionAsk(
  prompt: string,
  defaults: LeoQuestionBrief
): LeoQuestionBrief {
  const text = prompt.replace(/\s+/g, " ").trim()
  if (!text) return defaults

  const difficulty =
    LEO_DIFFICULTY_HINTS.find(([pattern]) => pattern.test(text))?.[1] ??
    defaults.difficulty
  const bloom =
    LEO_BLOOM_HINTS.find(([pattern]) => pattern.test(text))?.[1] ??
    defaults.bloom

  // A directive says what to change, not what the question is about.
  // Reading it as a topic is how the instruction ended up as the stem.
  if (isLeoQuestionEditDirective(text)) {
    return { coverage: defaults.coverage, difficulty, bloom }
  }

  const tokens = text.split(" ")
  let first = 0
  while (first < tokens.length && isLeoFramingToken(tokens[first])) first += 1
  let last = tokens.length
  while (
    last > first &&
    (isLeoFramingToken(tokens[last - 1]!) ||
      /^(and|or)$/i.test(tokens[last - 1]!))
  ) {
    last -= 1
  }
  const coverage = tokens
    .slice(first, last)
    .join(" ")
    .replace(/^[\s,.;:]+|[\s,.;:?!]+$/g, "")
    .trim()

  // "Make it harder" leaves nothing behind — that turn is about the level, and
  // the subject it applies to is the one already under discussion.
  // A short answer to Leo's post-draft scenario question adds context to the
  // existing objective. Treating "a patient on an inpatient ward" as a new
  // subject would make the next draft lose the teaching point.
  const scenarioReply = Boolean(defaults.coverage) &&
    /^(?:in|at|with|a|an|the|use|add|include|patient|adult|older)\b/i.test(text) &&
    !/\b(?:new topic|instead of|question about|item on)\b/i.test(text)
  const nextCoverage = scenarioReply &&
    !defaults.coverage.toLowerCase().includes(text.toLowerCase())
    ? defaults.coverage + "; scenario: " + text
    : coverage || defaults.coverage
  return { coverage: nextCoverage, difficulty, bloom }
}

// ─────────────────────────────────────────────────────────────────────────────
// Draft variants — Leo answers with options, not one silent overwrite
// ─────────────────────────────────────────────────────────────────────────────

export interface LeoQuestionDraftVariant {
  id: string
  /** Why pick this one over its sibling — the difference in one phrase. */
  angle: string
  patch: LeoQuestionDraftPatch
  /** Item review returned in the same Ask Leo response as this draft. */
  spottedReview?: { field: string; finding: string; action: string }
}

const BLOOM_LADDER: readonly LibraryBloomLevel[] = [
  "Remember",
  "Understand",
  "Apply",
  "Analyze",
  "Evaluate",
  "Create",
]

const LEO_VARIANT_ANGLE: Record<LibraryBloomLevel, string> = {
  Remember: "Straight recall",
  Understand: "Explains the mechanism",
  Apply: "Case vignette",
  Analyze: "Compares two explanations",
  Evaluate: "Defends a plan",
  Create: "Builds a plan",
}

/**
 * One draft per ask. Follow-up cards under the card collect the next detail.
 */
export function buildLeoQuestionDraftVariants(
  type: AuthoringQuestionType,
  brief: LeoQuestionBrief,
  folderName?: string
): LeoQuestionDraftVariant[] {
  const bloom =
    BLOOM_LADDER.includes(brief.bloom) ? brief.bloom : "Understand"
  return [
    {
      id: "leo-variant-1",
      angle: LEO_VARIANT_ANGLE[bloom],
      patch: buildLeoQuestionDraftForBrief(type, { ...brief, bloom }, folderName),
    },
  ]
}

/** Demo draft payload applied after the thinking animation (no Ask Leo panel). */
export function buildMockLeoQuestionDraft(
  type: AuthoringQuestionType,
  folderName?: string
): LeoQuestionDraftPatch {
  const draft = buildMockLeoQuestionDraftBody(type, folderName)
  return draft.title || !draft.leadIn
    ? draft
    : { ...draft, title: questionTitleFromLeadIn(draft.leadIn) }
}

function buildMockLeoQuestionDraftBody(
  type: AuthoringQuestionType,
  folderName?: string
): LeoQuestionDraftPatch {
  const tag = leoFolderTag(folderName)
  const baseTags = ["leo-draft", tag]
  const curriculum = AUTHORING_DEFAULT_CURRICULUM
  const general = AUTHORING_DEFAULT_GENERAL_SETTINGS

  switch (type) {
    case "mcq_multiple":
      return {
        leadIn:
          "A client with COPD is being discharged. Which actions belong in the teaching plan? Select all that apply.",
        options: [
          {
            id: "opt-leo-1",
            text: "Demonstrate pursed-lip breathing before activity",
            isCorrect: true,
            rationale: "",
          },
          {
            id: "opt-leo-2",
            text: "Tell the client to raise oxygen whenever they feel short of breath",
            isCorrect: false,
            rationale: "",
          },
          {
            id: "opt-leo-3",
            text: "Review signs that require a same-day call to the provider",
            isCorrect: true,
            rationale: "",
          },
          {
            id: "opt-leo-4",
            text: "Advise skipping the rescue inhaler to avoid dependence",
            isCorrect: false,
            rationale: "",
          },
          {
            id: "opt-leo-5",
            text: "Confirm the client can state their oxygen target range",
            isCorrect: true,
            rationale: "",
          },
        ],
        rationale:
          "COPD discharge teaching covers breathing technique, when to call, and the prescribed oxygen target. Raising oxygen without an order or holding the rescue inhaler is unsafe.",
        difficulty: "medium",
        bloom: "Analyze",
        cogLevel: "analysis",
        tags: baseTags,
        ...curriculum,
        ...general,
        points: AUTHORING_DEFAULT_POINTS,
      }
    case "true_false":
      return {
        leadIn:
          "A draft library item should remain invisible to respondents until it is published.",
        options: [
          { id: "opt-true", text: "True", isCorrect: true, rationale: "" },
          { id: "opt-false", text: "False", isCorrect: false, rationale: "" },
        ],
        rationale:
          "Draft and in-review states are author-facing only; published items are what respondents see.",
        difficulty: "easy",
        bloom: "Understand",
        cogLevel: "recall",
        tags: baseTags,
        ...curriculum,
        ...general,
        points: AUTHORING_DEFAULT_POINTS,
      }
    case "short_answer":
      return {
        leadIn: "What is the primary purpose of a question rationale field?",
        rationale:
          'Model answer: explain why the keyed response is correct and note acceptable variants (e.g. "scoring notes", "grading notes").',
        difficulty: "medium",
        bloom: "Understand",
        cogLevel: "recall",
        tags: baseTags,
        ...curriculum,
        ...general,
        points: AUTHORING_DEFAULT_POINTS,
      }
    case "numeric":
      return {
        leadIn:
          "If a process completes 3 of 4 required checkpoints, what fraction is complete?",
        numericValue: "0.75",
        numericTolerance: "0.01",
        numericUnits: "",
        rationale:
          "Three of four checkpoints ⇒ 3/4 = 0.75 within ±0.01 tolerance.",
        difficulty: "easy",
        bloom: "Apply",
        cogLevel: "application",
        tags: baseTags,
        ...curriculum,
        ...general,
        points: AUTHORING_DEFAULT_POINTS,
      }
    case "essay":
      return {
        leadIn:
          "Describe how you would validate a new assessment item before publishing it to the library.",
        rationale:
          "Rubric: (1) stem is unambiguous, (2) keyed answer or rubric is complete, (3) Bloom level matches intent, (4) peer review recorded.",
        difficulty: "hard",
        bloom: "Evaluate",
        cogLevel: "analysis",
        tags: baseTags,
        ...curriculum,
        ...general,
        points: AUTHORING_DEFAULT_POINTS,
      }
    case "fill_blank":
      return {
        leadIn:
          "Authors should add a {{blank}} so reviewers understand why the keyed response is correct.",
        fillBlankAnswers: [{ id: "blk-leo-1", accepted: "rationale" }],
        rationale: "Accepted: rationale (or scoring notes / grading notes).",
        difficulty: "medium",
        bloom: "Remember",
        cogLevel: "recall",
        tags: baseTags,
        ...curriculum,
        ...general,
        points: AUTHORING_DEFAULT_POINTS,
      }
    case "matching":
      return {
        leadIn: "Match each workflow state to its best description.",
        pairs: [
          {
            id: "pair-leo-1",
            left: "Draft",
            right: "Author-only; not eligible for use",
          },
          {
            id: "pair-leo-2",
            left: "In review",
            right: "Awaiting peer sign-off",
          },
          {
            id: "pair-leo-3",
            left: "Published",
            right: "Live for respondents",
          },
        ],
        rationale:
          "Each state maps to library visibility and review expectations.",
        difficulty: "medium",
        bloom: "Understand",
        cogLevel: "application",
        tags: baseTags,
        ...curriculum,
        ...general,
        points: AUTHORING_DEFAULT_POINTS,
      }
    case "ordering":
      return {
        leadIn:
          "Place the authoring steps in the order a coordinator typically follows.",
        orderedItems: [
          { id: "ord-leo-1", text: "Pick folder and question format" },
          { id: "ord-leo-2", text: "Write stem and keyed response" },
          { id: "ord-leo-3", text: "Add rationale and references" },
          { id: "ord-leo-4", text: "Submit for review" },
        ],
        rationale:
          "Folder/format first, then content, then metadata, then workflow handoff.",
        difficulty: "medium",
        bloom: "Apply",
        cogLevel: "application",
        tags: baseTags,
        ...curriculum,
        ...general,
        points: AUTHORING_DEFAULT_POINTS,
      }
    case "hotspot":
      return {
        leadIn:
          "On the reference diagram, select the region that represents the primary action control.",
        rationale:
          "The correct click lands inside the marked region on the diagram.",
        difficulty: "medium",
        bloom: "Apply",
        cogLevel: "application",
        tags: baseTags,
        ...curriculum,
        ...general,
        points: AUTHORING_DEFAULT_POINTS,
      }
    case "mcq_single":
    default:
      return {
        leadIn:
          "A client with new heart failure reports increasing dyspnea. Which nursing assessment is the priority?",
        options: [
          {
            id: "opt-leo-1",
            text: "Auscultate the lungs and check oxygen saturation",
            isCorrect: true,
            rationale: "",
          },
          {
            id: "opt-leo-2",
            text: "Ask the client to rate the dyspnea and continue the interview",
            isCorrect: false,
            rationale: "",
          },
          {
            id: "opt-leo-3",
            text: "Obtain a full diet history before the physical exam",
            isCorrect: false,
            rationale: "",
          },
          {
            id: "opt-leo-4",
            text: "Schedule teaching on fluid restriction for later in the shift",
            isCorrect: false,
            rationale: "",
          },
          {
            id: "opt-leo-5",
            text: "Document the complaint and reassess at the next vital signs round",
            isCorrect: false,
            rationale: "",
          },
        ],
        rationale:
          "New or worsening dyspnea in heart failure needs airway and oxygenation first. History and teaching wait until the client is stable.",
        references: [
          {
            ...createAuthoringReference("ref-leo-1", "citation"),
            citation:
              "Internal library authoring guide — review checklist (demo).",
          },
        ],
        difficulty: "medium",
        bloom: "Apply",
        cogLevel: "application",
        tags: baseTags,
        ...curriculum,
        ...general,
        points: AUTHORING_DEFAULT_POINTS,
      }
  }
}
